import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { useCan } from '../contexts/AuthContext';
import {
    BadgeEuro, Plus, Pencil, Trash2, Search, MapPin, DollarSign, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TarifAOT() {
    const { canWrite, canDelete } = useCan('tarif_aot');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null); // null | 'new' | tarif object
    const [draft, setDraft] = useState({ ville: '', tarif_simple: '', tarif_monte_meuble: '' });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/tarif-aot`, { withCredentials: true });
            setItems(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) => (it.ville || '').toLowerCase().includes(q));
    }, [items, search]);

    const startNew = () => {
        setEditing('new');
        setDraft({ ville: '', tarif_simple: '', tarif_monte_meuble: '' });
    };

    const startEdit = (it) => {
        setEditing(it);
        setDraft({
            ville: it.ville,
            tarif_simple: it.tarif_simple,
            tarif_monte_meuble: it.tarif_monte_meuble,
        });
    };

    const close = () => { setEditing(null); setDraft({ ville: '', tarif_simple: '', tarif_monte_meuble: '' }); };

    const save = async () => {
        if (!draft.ville?.trim()) { toast.error('La ville est requise'); return; }
        setSaving(true);
        try {
            const payload = {
                ville: draft.ville.trim(),
                tarif_simple: Number(draft.tarif_simple || 0),
                tarif_monte_meuble: Number(draft.tarif_monte_meuble || 0),
            };
            if (editing === 'new') {
                await axios.post(`${API_URL}/api/tarif-aot`, payload, { withCredentials: true });
                toast.success('Tarif ajouté');
            } else {
                await axios.put(`${API_URL}/api/tarif-aot/${editing.id}`, payload, { withCredentials: true });
                toast.success('Tarif mis à jour');
            }
            close();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (it) => {
        if (!window.confirm(`Supprimer le tarif pour ${it.ville} ?`)) return;
        try {
            await axios.delete(`${API_URL}/api/tarif-aot/${it.id}`, { withCredentials: true });
            toast.success('Tarif supprimé');
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="space-y-6 animate-fadeIn" data-testid="tarif-aot-page">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Tarif AOT
                        </h1>
                        <p className="text-gray-500 mt-0.5 text-sm">
                            Tarification des autorisations de stationnement par ville
                        </p>
                    </div>
                    <Button
                        onClick={startNew}
                        disabled={!canWrite}
                        title={!canWrite ? 'Accès refusé : permission Écriture requise' : ''}
                        className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="tarif-aot-add-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Nouvelle ville
                    </Button>
                </div>

                {/* Search */}
                <Card className="shadow-sm">
                    <CardContent className="p-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Rechercher une ville…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-11 border-0 focus-visible:ring-0 text-base"
                                data-testid="tarif-aot-search"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* List */}
                <Card className="shadow-sm">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <DollarSign className="w-5 h-5 text-blue-600" />
                            <h2 className="text-lg font-semibold text-[#1B365D]">
                                Liste des tarifs ({filtered.length}{filtered.length !== items.length ? ` / ${items.length}` : ''})
                            </h2>
                        </div>

                        {loading ? (
                            <div className="py-10 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="py-12 text-center">
                                <BadgeEuro className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                                <p className="text-sm text-gray-400">
                                    {items.length === 0
                                        ? 'Aucun tarif. Cliquez sur « Nouvelle ville » pour ajouter.'
                                        : 'Aucune ville ne correspond à votre recherche.'}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full" data-testid="tarif-aot-table">
                                    <thead>
                                        <tr className="border-b text-gray-400 text-xs uppercase tracking-wide">
                                            <th className="text-left py-3 px-3 font-medium">Ville</th>
                                            <th className="text-center py-3 px-3 font-medium">Tarif AOT simple</th>
                                            <th className="text-center py-3 px-3 font-medium">Tarif AOT monte-meuble</th>
                                            <th className="text-right py-3 px-3 font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((it) => (
                                            <tr key={it.id} className="border-b last:border-0 hover:bg-gray-50/40">
                                                <td className="py-4 px-3">
                                                    <span className="inline-flex items-center gap-2 text-[#1B365D] font-medium">
                                                        <MapPin className="w-4 h-4 text-blue-500" />
                                                        {it.ville}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-3 text-center font-bold text-[#1B365D]">
                                                    {it.tarif_simple}
                                                </td>
                                                <td className="py-4 px-3 text-center font-bold text-[#1B365D]">
                                                    {it.tarif_monte_meuble}
                                                </td>
                                                <td className="py-4 px-3 text-right">
                                                    <div className="inline-flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => startEdit(it)}
                                                            disabled={!canWrite}
                                                            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-[#1B365D] disabled:opacity-40 disabled:cursor-not-allowed"
                                                            title={!canWrite ? 'Accès refusé : permission Écriture requise' : 'Modifier'}
                                                            data-testid={`tarif-aot-edit-${it.id}`}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(it)}
                                                            disabled={!canDelete}
                                                            className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                                            title={!canDelete ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                            data-testid={`tarif-aot-delete-${it.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Create / Edit dialog */}
            <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) close(); }}>
                <DialogContent className="sm:max-w-md" data-testid="tarif-aot-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <BadgeEuro className="w-5 h-5 text-blue-600" />
                            {editing === 'new' ? 'Nouveau tarif AOT' : 'Modifier le tarif'}
                        </DialogTitle>
                        <DialogDescription>
                            Indiquez la ville et les deux tarifs (simple et avec monte-meuble).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="text-xs uppercase text-gray-500 tracking-wide">Ville</label>
                            <Input
                                value={draft.ville}
                                onChange={(e) => setDraft({ ...draft, ville: e.target.value })}
                                placeholder="Ex: Paris"
                                className="mt-1"
                                data-testid="tarif-aot-ville-input"
                                autoFocus
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase text-gray-500 tracking-wide">Tarif AOT simple (€)</label>
                                <Input
                                    type="number" step="0.01" min="0"
                                    value={draft.tarif_simple ?? ''}
                                    onChange={(e) => setDraft({ ...draft, tarif_simple: e.target.value })}
                                    placeholder="50"
                                    className="mt-1"
                                    data-testid="tarif-aot-simple-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase text-gray-500 tracking-wide">Tarif AOT monte-meuble (€)</label>
                                <Input
                                    type="number" step="0.01" min="0"
                                    value={draft.tarif_monte_meuble ?? ''}
                                    onChange={(e) => setDraft({ ...draft, tarif_monte_meuble: e.target.value })}
                                    placeholder="100"
                                    className="mt-1"
                                    data-testid="tarif-aot-mm-input"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={close} disabled={saving}>Annuler</Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="tarif-aot-save-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
